<template>
  <b-container fluid>
    <b-row class="justify-content-md-center">
      <b-col lg="8" class="pb-5">
        <!-- LU Title -->
        <div class="h5 display-4 font-weight-bold mb-3 header__lu-title">
          {{ learningUnit ? learningUnit.name : '' }}
        </div>

        <!-- Button "Back To previous page" -->
        <div class="mb-1">
          <b-button
            variant="outline-primary"
            class="mb-4 btn__previous-page"
            @click="gotoPreviousPage()"
          >
            <span class="btn-wrapper--icon">
              <font-awesome-icon icon="arrow-left" />
            </span>
            <span class="btn-wrapper--label"> Back to previous page</span>
          </b-button>
        </div>

        <div class="d-flex flex-wrap align-items-start justify-content-between">
          <div class="font-weight-bold my-2">View submission and feedback</div>

          <b-form-group>
            <BaseSelect
              :label="'Show :'"
              :style-select="'display: inline; width: 75%'"
              :options-select="optionsStatus"
              :value-field="'id'"
              :default-value="null"
              :text-field="'name'"
              @onFilterSelected="onFilterSelected"
            />
          </b-form-group>
        </div>

        <div class="container__metric-statuses d-flex justify-content-md-around my-4">
          <BaseAssessmentStatusMetric
            v-for="(status, index) of assessmentStatusesCount"
            :key="`assessmentStatusMetric-${index}`"
            :color-class="status.colorClass"
            :status-count="status.count"
            :status-text="status.text"
          />
        </div>

        <b-row v-if="isLoading" class="justify-content-md-center">
          <b-col lg="6">
            <div class="d-flex justify-content-center">
              <b-spinner label="Loading..." variant="dark" />
            </div>
          </b-col>
        </b-row>

        <SubmissionAccordion
          v-for="(learner, index) in learners"
          :key="`submissionAccordion-${learner.id}`"
          :post="post"
          :post-schedule="postSchedule"
          :learner="learner"
          :teacher="user"
          :learning-unit-id="params.learningUnitId"
          :initial-show-collapse="index === 0"
        />

        <div v-if="!isLoading && !learners.length" class="text-dark text-center mt-5">
          No assessment submissions found
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { map } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  LearningUnit,
  Section,
  Post,
  PostResponse,
  Status,
  User,
  Learner,
  AssessmentStatus,
  PostSchedule
} from '@/models';
import { scrollBottomCheckerMixin } from '@/mixins';
import { BaseSelect } from '@/components/base-components';
import SubmissionAccordion from './components/SubmissionAccordion.vue';
import { BaseAssessmentStatusMetric } from '@/components/base-components';

export default {
  name: 'PageTeachingReview',

  components: {
    FontAwesomeIcon,
    SubmissionAccordion,
    BaseSelect,
    BaseAssessmentStatusMetric
  },

  mixins: [scrollBottomCheckerMixin],

  data() {
    return {
      isInit: false,
      isLoading: false,
      learnerIds: [],
      selectedStatusId: null // null = All, 1 = Not Submitted, 2 = Review Pending, 3 = Feedback, 4 = Passed
      // isFetchAllSubmission: false,
      // submissionCountCurrent: 0,
      // submissionLimitPerScroll: 10,
    };
  },

  computed: {
    params() {
      const { learningUnitId, sectionId, postId } = this.$route.params;

      return {
        learningUnitId: Number.parseInt(learningUnitId),
        sectionId: Number.parseInt(sectionId),
        postId: Number.parseInt(postId)
      };
    },

    user() {
      return User.query().first();
    },

    learningUnit() {
      return LearningUnit.find(this.params.learningUnitId);
    },

    section() {
      return Section.find(this.params.sectionId);
    },

    post() {
      return Post.query()
        .where('id', this.params.postId)
        .with('additional_criterion')
        .with('learning_outcome')
        .with('vimeo')
        .with('assessment_statuses')
        .first();
    },

    learners() {
      return Learner.query().whereIdIn(this.learnerIds).get();
    },

    postResponses() {
      return PostResponse.all();
    },

    assessmentStatusesCount() {
      const assessmentStats = {
        [Status.REVIEW_PENDING]: {
          colorClass: 'warning',
          count: 0,
          text: Status.REVIEW_PENDING
        },
        [Status.NOT_SUBMITTED]: {
          colorClass: 'danger',
          count: 0,
          text: Status.NOT_SUBMITTED
        },
        [Status.FEEDBACK]: {
          colorClass: 'info',
          count: 0,
          text: Status.FEEDBACK
        },
        [Status.PASSED]: {
          colorClass: 'success',
          count: 0,
          text: Status.PASSED
        }
      };

      if (this.post && this.section) {
        const countUserEnrolled = this.section.countEnrolledUser();
        let countUserHasAssessmentStatus = 0;

        for (const statusName in assessmentStats) {
          if (Object.hasOwnProperty.call(assessmentStats, statusName)) {
            const assessmentStat = assessmentStats[statusName];
            const status = Status.name(statusName);

            if (status) {
              const countStatus = AssessmentStatus.countStatusInPostInSection(
                status.id,
                this.post.id,
                this.section.id
              );

              countUserHasAssessmentStatus += countStatus;

              this.$set(assessmentStat, 'count', countStatus);
            }

            this.$set(assessmentStats, statusName, assessmentStat);
          }
        }

        const countNotSubmitted = countUserEnrolled - countUserHasAssessmentStatus;

        this.$set(
          assessmentStats[Status.NOT_SUBMITTED],
          'count',
          countNotSubmitted >= 0 ? countNotSubmitted : 0
        );

        return [
          assessmentStats[Status.REVIEW_PENDING],
          assessmentStats[Status.NOT_SUBMITTED],
          assessmentStats[Status.FEEDBACK],
          assessmentStats[Status.PASSED]
        ];
      }
      // const statuses = Status.query().all();
      // const priorStatuses = Object.keys(statsClass);
      // const usersEnrollmentCount = Section.find(this.params.sectionId)
      //   ? Section.find(this.params.sectionId).number_of_learners
      //   : 0;
      // const allAssessmentCount = AssessmentStatus.query().count();
      // const notSubmittedStatCount = usersEnrollmentCount - allAssessmentCount;

      // for (let status of statuses) {
      //   const assessmentCount = AssessmentStatus.query()
      //     .where("status_id", status.id)
      //     .count();

      //   /* No one submitted assessment */
      //   if (allAssessmentCount === 0) {
      //     assessmentStats.push({
      //       colorClass: statsClass[Status.NOT_SUBMITTED],
      //       count: notSubmittedStatCount,
      //       text: Status.NOT_SUBMITTED,
      //     });
      //     break;
      //   }
      //   /* all statuses (exclude NOT_SUBMITTED) */
      //   if (assessmentCount > 0)
      //     assessmentStats.push({
      //       colorClass: statsClass[status.name],
      //       count: assessmentCount,
      //       text: status.name,
      //     });
      //   /* Not Submitted status */
      //   if (notSubmittedStatCount > 0) {
      //     const hasNotSubmitted = assessmentStats.some(
      //       (assessmentStat) => assessmentStat.text === Status.NOT_SUBMITTED
      //     );
      //     !hasNotSubmitted &&
      //       assessmentStats.push({
      //         colorClass: statsClass[Status.NOT_SUBMITTED],
      //         count: notSubmittedStatCount,
      //         text: Status.NOT_SUBMITTED,
      //       });
      //   }
      // }
      // /* Sorting statuses */
      // const sortedAssessmentStats = assessmentStats.sort((a, b) => {
      //   return priorStatuses.indexOf(a.text) - priorStatuses.indexOf(b.text);
      // });

      return assessmentStats;
    },

    statuses() {
      return Status.assessmentStatus();
    },

    postSchedule() {
      return this.params.sectionId
        ? PostSchedule.query().where('section_id', this.params.sectionId).first()
        : null;
    },

    optionsStatus() {
      const all = {
        id: null,
        name: 'All'
      };
      return [all, ...this.statuses];
    }
  },

  watch: {
    user: {
      handler(user) {
        if (user) {
          this.init();
        }
      }
    }
  },

  mounted() {
    if (this.user) {
      this.init();
    }
  },

  methods: {
    async init() {
      if (!this.isInit) {
        this.isInit = true;
        this.isLoading = true;

        this.resetData();

        await this.fetch();

        this.isLoading = false;

        // this.$_scrollBottomCheckerMixin_scroll(
        //   this.fetchLearnerStatus
        // );
      }
    },

    fetch() {
      Section.deleteAll();
      Post.deleteAll();
      AssessmentStatus.deleteAll();

      return Promise.all([
        LearningUnit.api().fetchByLearningUnitId(this.params.learningUnitId),
        Status.api().fetch(),
        PostSchedule.api().findForTeacher(this.params.sectionId),
        this.fetchLearnerStatus()
      ]);
    },

    async fetchLearnerStatus() {
      // if (this.isFetchAllSubmission) return;

      if (!this.user?.id || !this.params?.sectionId || !this.params?.postId) return;

      this.isLoading = true;

      try {
        let { response } = await Post.api().fetchStatusInSection(
          this.params.postId,
          this.params.sectionId
        );

        const { data = [] } = response;

        if (data.length) {
          this.learnerIds = map(data, 'user.id');
        }
      } catch (error) {
        this.$toast.error('Cannot load data, please try again');
      }

      this.isLoading = false;

      return;
    },

    onFilterSelected(value) {
      this.selectedStatusId = value;
      this.resetData();
      this.fetchLearnerStatus();
    },

    // onSubmitSuccess(index, { statusId } = {}) {
    //   const originalData = this.learners[index];

    //   this.learners.splice(index, 1, {
    //     ...originalData,
    //     // latestEvaluationId,
    //     status: Status.find(statusId),
    //     // postResponses: [
    //     //   ...originalData.postResponses,
    //     //   PostResponse.query()
    //     //     .with(["user", "assessment_evaluations"])
    //     //     .whereId(latestEvaluationId)
    //     //     .first(),
    //     // ],
    //   });
    // },

    // async onEditSuccess(index, postResponseId) {
    // await PostResponse.api().fetchById(postResponseId);

    // const originalData = this.learners[index];

    // const newPostResponseList = originalData.postResponses.map(
    //   (postResponses) => {
    //     if (postResponses.id == postResponseId) {
    //       return PostResponse.query()
    //         .with(["user", "assessment_evaluations"])
    //         .whereId(postResponseId)
    //         .first();
    //     } else {
    //       return postResponses;
    //     }
    //   }
    // );

    // this.learners.splice(index, 1, {
    //   ...originalData,
    //   postResponses: newPostResponseList,
    // });
    // },

    resetData() {
      // this.isFetchAllSubmission = false;
      // this.submissionCountCurrent = 0;
      // this.submissionLimitPerScroll = 10;
      this.learnerIds = [];
    },

    gotoPreviousPage() {
      this.$router.push({
        name: 'design-planning/teaching-feed',
        params: {
          learningUnitId: this.params.learningUnitId,
          sectionId: this.params.sectionId
        },
        query: {
          ...this.$route.query
        }
      });
    }
  }
};
</script>
